// File: frontend/src/App.tsx
// src/App.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useParams, useLocation } from "react-router-dom";
import { trackPageView } from "./utils/analytics";
import "./App.css";
import {
  Header,
  LeftPane,
  MainContent,
  RightPane,
  Footer,
} from "./layouts";
import { ErrorDisplay, SpinnerLoading } from "./utils";
import { useEntries, useMatches } from "./hooks";
import { useDocumentMeta } from "./hooks/useDocumentMeta";
import { useUrlPage, pageSuffixes, useIsEntryDeepLink, useFocusEntryId, useExitFocus } from "./hooks/useUrlPage";
import { useFocusedEntry } from "./hooks/useFocusedEntry";
import { EntryPermalinkPage } from "./pages/entry/EntryPermalinkPage";
import { useCollapsedOldEntries, CollapseEntryFetcher } from "./hooks/useCollapsedOldEntries";
import { usePeople } from "./hooks/usePeople";
import { useClubs } from "./hooks/useClubs";
import { useTeams } from "./hooks/useTeams";
import { useCustomTopics } from "./hooks/useCustomTopics";
import { useSystemChannelTopics } from "./hooks/useSystemChannelTopics";
import { getDateRangeForFilter, formatDateForAPI, formatInstantForAPI, MatchFilter } from "./hooks/useMatches";
import { useMatchSelection } from "./hooks/useMatchSelection";
import { postNewEntry } from "./services/entryPostService";
import { fetchEntriesByMatchId, fetchTotalEntryCount } from "./services";
import { resolveClubName } from "./services/matchService";
import { formatMatchDateLocal } from "./utils/dateUtils";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme, ThemeType } from "./context/ThemeContext";
import { PreferencesProvider, usePreferences } from "./context/PreferencesContext";
import { SiteSettingsProvider, useSiteSettings } from "./context/SiteSettingsContext";
import { ENTRIES_PER_PAGE, MATCHES_PER_PAGE } from "./constants";
import { NEW_ENTRY_POLL_INTERVAL_MS } from "./constants/settings.constants";
import { ProfilePage } from "./pages/profile";
import { SettingsPage } from "./pages/settings";
import { TrashPage } from "./pages/trash";
import { DraftsPage } from "./pages/drafts";
import { ModlogPage } from "./pages/modlog";
import { AdminPage } from "./pages/admin";
import { PersonPage } from "./pages/person";
import { ClubPage } from "./pages/club";
import { TeamPage } from "./pages/team";
import { CustomTopicPage } from "./pages/customTopic";
import { ExternalLinkWarning } from "./components/entries";
import { ThemeEnum } from "./models/enums";
import { MatchStatusEnum } from "./models/enums/MatchStatusEnum";
import { SearchResultsPage } from "./pages/search";
import { MessagesPage } from "./pages/messages";
import { AnnouncementBanner } from "./components/common/AnnouncementBanner";
import { ConnectivityProvider } from "./context/ConnectivityContext";
import { ConnectivityBanner } from "./components/common/ConnectivityBanner";
import { UpdateAvailableBanner } from "./components/common/UpdateAvailableBanner";
import { PullToRefresh } from "./components/common/PullToRefresh";
import { LoadingOverlay } from "./components/common/LoadingOverlay";
import { FeedPage } from "./pages/feed";
import { StatisticsPage } from "./pages/statistics";
import { MaintenancePage } from "./pages/maintenance";
import { VerifyEmailPage } from "./pages/verify-email";
import { ResetPasswordPage } from "./pages/reset-password";
import { ContactPage } from "./pages/contact";
import { LegalPage, CookiePreferencesPage } from "./pages/legal";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import { CookieConsentBanner } from "./components/common/CookieConsentBanner";
import { DomainTabBar } from "./components/common/DomainTabBar";
import { PaneFilterBar } from "./components/common/PaneFilterBar";

// Person page wrapper — extracts slug from /kisi/:slug route
const PersonPageWrapper: React.FC<{ onMobileBack?: () => void }> = ({ onMobileBack }) => {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <div style={{ padding: 32, color: "var(--color-error)" }}>Geçersiz kişi adresi.</div>;
  return <PersonPage personSlug={slug} onMobileBack={onMobileBack} />;
};

// Club page wrapper — accepts onEntryPosted so App.tsx can refresh the club list
const ClubPageWrapper: React.FC<{ onEntryPosted?: () => void; onMobileBack?: () => void }> = ({ onEntryPosted, onMobileBack }) => {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <div style={{ padding: 32, color: "var(--color-error)" }}>Geçersiz kulüp adresi.</div>;
  return <ClubPage clubSlug={slug} onEntryPosted={onEntryPosted} onMobileBack={onMobileBack} />;
};

// Team page wrapper — accepts onEntryPosted so App.tsx can refresh the team list
const TeamPageWrapper: React.FC<{ onEntryPosted?: () => void; onMobileBack?: () => void }> = ({ onEntryPosted, onMobileBack }) => {
  // clubSlug is present only on the legacy two-segment route; TeamPage uses it
  // to call the legacy endpoint, which redirects to the canonical address.
  const { clubSlug, teamSlug } = useParams<{ clubSlug?: string; teamSlug: string }>();
  if (!teamSlug) return <div style={{ padding: 32, color: "var(--color-error)" }}>Geçersiz takım adresi.</div>;
  return <TeamPage clubSlug={clubSlug} teamSlug={teamSlug} onEntryPosted={onEntryPosted} onMobileBack={onMobileBack} />;
};

// Custom topic page wrapper — accepts onEntryPosted so App.tsx can refresh the topic list
const CustomTopicPageWrapper: React.FC<{ reloadToken?: number; onEntryPosted?: () => void; onMobileBack?: () => void }> = ({ reloadToken, onEntryPosted, onMobileBack }) => {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <div style={{ padding: 32, color: "var(--color-error)" }}>Geçersiz başlık adresi.</div>;
  return <CustomTopicPage topicSlug={slug} reloadToken={reloadToken} onEntryPosted={onEntryPosted} onMobileBack={onMobileBack} />;
};

// Profile page wrapper to extract userId from URL
const ProfilePageWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  
  // Extract userId from slug format: "username--123"
  const userId = slug ? parseInt(slug.split("--").pop() || "0", 10) : 0;

  if (!userId) {
    return <ErrorDisplay error="Invalid profile URL" />;
  }

  return <ProfilePage userId={userId} />;
};

const AppContent: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { theme, setTheme } = useTheme();
  const { preferences } = usePreferences();
  const { maintenanceEnabled } = useSiteSettings();
  const location = useLocation();
  
  // Track page views on every route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Track if initial theme sync has been done
  const initialThemeSyncDone = useRef(false);

  // Sync theme from preferences ONLY on initial load (not on every preference change)
  useEffect(() => {
    // Only sync on first load when preferences arrive
    if (preferences?.theme && !initialThemeSyncDone.current) {
      const themeMap: Record<ThemeEnum, ThemeType> = {
        [ThemeEnum.LIGHT]: "light",
        [ThemeEnum.DARK]: "dark",
        [ThemeEnum.PAPER]: "paper",
        [ThemeEnum.TANGO]: "tango",
        [ThemeEnum.FLAMINGO]: "flamingo",
        [ThemeEnum.COMOLOKKO]: "comolokko",
        [ThemeEnum.LOKUM]: "lokum",
        [ThemeEnum.MERCAN]: "mercan",
        [ThemeEnum.GOKYUZU]: "gokyuzu",
        // Dynamic fan theme — ThemeContext resolves the club colours and falls
        // back to light on its own when the user has no favourite club left.
        [ThemeEnum.TARAFTAR]: "taraftar",
      };
      const backendTheme = themeMap[preferences.theme];
      
      // Only update if different from current theme (respects localStorage)
      if (backendTheme !== theme) {
        setTheme(backendTheme);
      }
      initialThemeSyncDone.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences?.theme]); // Removed setTheme from deps to prevent loops

  // Check if we're on a special page (profile, settings, or admin)
  const isProfilePage = location.pathname.startsWith("/yazar/");
  const isPersonPage = location.pathname.startsWith("/kisi/");
  const isClubPage = location.pathname.startsWith("/kulup/");
  const isTeamPage = location.pathname.startsWith("/takim/");
  const isTopicPage = location.pathname.startsWith("/gundem/");
  const isExternalLinkPage = location.pathname.startsWith("/dis-link");
  const isSettingsPage = location.pathname.startsWith("/ayarlar");
  const isTrashPage = location.pathname.startsWith("/cop-kutusu");
  const isDraftsPage = location.pathname.startsWith("/taslaklar");
  const isModlogPage = location.pathname.startsWith("/moderasyon-gunlugu");
  const isAdminPage = location.pathname.startsWith("/admin");
  // Search results page: root path with ?q= query param (sözlük convention URL)
  // Search results page: root path with a keyword or any advanced filter param.
  // Filter-only searches (author / date range without a keyword) have no q= in
  // the URL, so checking only for "q=" hid the results page for them.
  const searchQueryParams = new URLSearchParams(location.search);
  const isSearchPage =
    location.pathname === "/" &&
    (searchQueryParams.has("q") ||
      searchQueryParams.has("yazar") ||
      searchQueryParams.has("baslangic") ||
      searchQueryParams.has("bitis"));
  const isMessagesPage = location.pathname.startsWith("/mesajlar");
  const isFeedPage = location.pathname.startsWith("/akis");
  const isStatisticsPage = location.pathname.startsWith("/istatistikler");
  // Entry permalink route: /entry/{id} resolves itself to a topic page and
  // replaces the history entry, so nothing else should render for it.
  const isEntryPermalinkPage = location.pathname.startsWith("/entry/");
  // Email verification page — standalone, no header/menu needed
  const isVerifyEmailPage = location.pathname.startsWith("/email-dogrula");
  const isResetPasswordPage = location.pathname.startsWith("/sifre-sifirla");
  // Contact page — public, no auth required
  const isContactPage = location.pathname.startsWith("/iletisim");
  // Legal pages — public, no auth required. A visitor has to be able to read
  // the terms before deciding whether to register.
  const isTermsPage = location.pathname.startsWith("/kullanim-kosullari");
  const isPrivacyPage = location.pathname.startsWith("/gizlilik-politikasi");
  const isCookiePolicyPage = location.pathname.startsWith("/cerez-politikasi");
  const isCookiePrefsPage = location.pathname.startsWith("/cerez-tercihleri");
  // Community rules ("Hamur Abi Kuralları") — served by the same LegalPage
  const isRulesPage = location.pathname.startsWith("/hamur-abi-kurallari");
  const isLegalPage = isTermsPage || isPrivacyPage || isCookiePolicyPage || isCookiePrefsPage || isRulesPage;

  // Determine showRookieEntries value - default to true if not set or user not authenticated
  const showRookieEntries = preferences?.showRookieEntries !== false;

  // Page sizes — fall back to constants when preferences are not loaded yet
  const entriesPerPage = preferences?.entriesPerPage ?? ENTRIES_PER_PAGE;
  const matchesPerPage = preferences?.matchesPerPage ?? MATCHES_PER_PAGE;

  const {
    matches,
    loading: loadingMatches,
    error: matchesError,
    pagination: matchesPagination,
    loadMatches: paginateMatches,
    refreshMatches,
    loadMoreMatches,
    isLoadingMore: isLoadingMoreMatches,
    filter,
    applyFilter,
    sortBy,
    setSortBy,
  } = useMatches(showRookieEntries, matchesPerPage);

  // Tracks whether the very first matches load has completed. Before that,
  // there is no content on screen to dim, so the app shows the plain
  // SpinnerLoading (as before). After it, all refreshes and transitions use
  // the transparent dim overlay on top of the live, still-mounted content.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!loadingMatches) setHasLoadedOnce(true);
  }, [loadingMatches]);

  // People list for the left pane "Kişi" mode
  const {
    people,
    loadPeople,
    loadMorePeople,
    isLoadingMore: isLoadingMorePeople,
    totalPages: peopleTotalPages,
    currentPage: peopleCurrentPage,
    sortBy: peopleSortBy,
    handleSortChange: handlePeopleSortChange,
    countryFilter: peopleCountryFilter,
    handleCountryFilterChange: handlePeopleCountryFilterChange,
  } = usePeople(false, showRookieEntries);

  // Clubs list for the left pane "Kulüp" mode
  const {
    clubs,
    loadClubs,
    loadMoreClubs,
    refreshClubs,
    isLoadingMore: isLoadingMoreClubs,
    totalPages: clubsTotalPages,
    currentPage: clubsCurrentPage,
    sortBy: clubsSortBy,
    handleSortChange: handleClubsSortChange,
    countryFilter: clubsCountryFilter,
    handleCountryFilterChange: handleClubsCountryFilterChange,
  } = useClubs(false, showRookieEntries);

  const [selectedClubId, setSelectedClubId] = React.useState<number | null>(null);
  const handleSelectClub = (clubId: number) => setSelectedClubId(clubId);

  // Teams list for the left pane "Takım" mode
  const {
    teams,
    loadTeams,
    loadMoreTeams,
    refreshTeams,
    isLoadingMore: isLoadingMoreTeams,
    totalPages: teamsTotalPages,
    currentPage: teamsCurrentPage,
    sortBy: teamsSortBy,
    handleSortChange: handleTeamsSortChange,
    countryFilter: teamsCountryFilter,
    handleCountryFilterChange: handleTeamsCountryFilterChange,
  } = useTeams(false, showRookieEntries);

  const [selectedTeamId, setSelectedTeamId] = React.useState<number | null>(null);
  const handleSelectTeam = (teamId: number) => setSelectedTeamId(teamId);

  // Custom topics list for the left pane "Özel" mode
  const {
    topics,
    loadTopics,
    loadMoreTopics,
    refreshTopics,
    shuffleTopics,
    isLoadingMore: isLoadingMoreTopics,
    totalPages: topicsTotalPages,
    currentPage: topicsCurrentPage,
    sortBy: topicsSortBy,
    handleSortChange: handleTopicsSortChange,
    channelFilter: topicsChannelFilter,
    handleChannelFilterChange: handleTopicsChannelFilterChange,
  } = useCustomTopics(false, showRookieEntries);

  // İstikram / Bırak — same underlying custom-topic domain as Gündem above,
  // filtered server-side to topics whose FIRST channel is that reserved
  // system channel (see useSystemChannelTopics / getSystemChannelTopics).
  // Kept as two separate hook instances (not a shared list + client filter)
  // so each tab paginates and sorts independently, same as every other domain.
  const {
    topics: istikramTopics,
    loadTopics: loadIstikramTopics,
    loadMoreTopics: loadMoreIstikramTopics,
    refreshTopics: refreshIstikramTopics,
    shuffleTopics: shuffleIstikramTopics,
    isLoadingMore: isLoadingMoreIstikramTopics,
    totalPages: istikramTopicsTotalPages,
    currentPage: istikramTopicsCurrentPage,
    sortBy: istikramTopicsSortBy,
    handleSortChange: handleIstikramTopicsSortChange,
  } = useSystemChannelTopics("istikram", false, showRookieEntries);

  const {
    topics: birakTopics,
    loadTopics: loadBirakTopics,
    loadMoreTopics: loadMoreBirakTopics,
    refreshTopics: refreshBirakTopics,
    shuffleTopics: shuffleBirakTopics,
    isLoadingMore: isLoadingMoreBirakTopics,
    totalPages: birakTopicsTotalPages,
    currentPage: birakTopicsCurrentPage,
    sortBy: birakTopicsSortBy,
    handleSortChange: handleBirakTopicsSortChange,
  } = useSystemChannelTopics("birak", false, showRookieEntries);

  const [selectedTopicId, setSelectedTopicId] = React.useState<number | null>(null);
  // Bumped on every Gündem row click (and on the pane's Yenile) and handed to
  // CustomTopicPage. Clicking the row of the topic that is already open keeps
  // the URL identical, so the route does not remount and the page used to keep
  // showing its stale entry list — the pane badge said "1" while the page still
  // said "Henüz tanım yok.". A changed token forces the entry refetch.
  const [topicReloadToken, setTopicReloadToken] = React.useState(0);
  const handleSelectTopic = (topicId: number) => {
    setSelectedTopicId(topicId);
    setTopicReloadToken((prev) => prev + 1);
  };

  const [selectedPersonId, setSelectedPersonId] = React.useState<number | null>(null);
  // paneMode is lifted from LeftPane so it survives SpinnerLoading unmount/remount.
  // Without this, paneMode resets to "matches" after every people/clubs/teams refresh,
  // causing useMatchSelection to navigate away to a match URL on re-mount.
  // Default landing pane is "topics" (Gündem) — changed from "matches" per product decision.
  const [paneMode, setPaneMode] = useState<"matches" | "people" | "clubs" | "teams" | "topics" | "istikram" | "birak">("topics");
  // Tracks whether a manual Yenile refresh for people/clubs/teams is in progress.
  // While true the app shows the global dim LoadingOverlay on top of the layout
  // (the layout stays mounted — no unmount/remount), so all three panes behave
  // identically during their own refresh.
  const [isRefreshingPane, setIsRefreshingPane] = useState(false);

  // Tracks whether the matches-pane Yenile refresh (handleRefresh) is in
  // progress, so the dim overlay covers the whole refresh (matches + entries).
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  // Lazy pane loading: the matches pane loads eagerly (it is the default visible pane),
  // while people/clubs/teams are fetched only the first time the user switches to them and
  // then cached for the rest of the session. This avoids loading all four panes' data on
  // every home-page load, which was the main cause of the slow refresh.
  const loadedPanesRef = useRef<Set<string>>(new Set(["matches"]));
  useEffect(() => {
    if (loadedPanesRef.current.has(paneMode)) return;
    loadedPanesRef.current.add(paneMode);
    if (paneMode === "people")        loadPeople();
    else if (paneMode === "clubs")    loadClubs("recentEntryCount", 0);
    else if (paneMode === "teams")    loadTeams("recentEntryCount", 0);
    else if (paneMode === "topics")   loadTopics("recentEntryCount", 0);
    else if (paneMode === "istikram") loadIstikramTopics("recentEntryCount", 0);
    else if (paneMode === "birak")    loadBirakTopics("recentEntryCount", 0);
  }, [paneMode, loadPeople, loadClubs, loadTeams, loadTopics, loadIstikramTopics, loadBirakTopics]);

  const handleSelectPerson = (personId: number) => {
    setSelectedPersonId(personId);
  };

  // Re-baseline the "new entry" counter after a pane refresh. People/clubs/
  // teams/topics refreshes do NOT reload matches (which is what normally
  // re-baselines the counter via the [matches] effect), so the baseline must be
  // recomputed here — otherwise the stale baseline makes the next poll tick
  // immediately re-show the badge.
  //
  // Ticket 1d: this used to call getDateRangeForFilter directly and bail out on
  // `if (sd && ed)`. The default date filter is "all" (see loadFilterFromStorage),
  // for which that helper deliberately returns null/null — so the guard was false
  // on every single call and the baseline was never recomputed at all. Yenile
  // cleared the badge, the baseline stayed where it was, and 30 seconds later the
  // same number came back, click after click. resolveCountDateRange is the shared
  // helper that already resolves "all" to a real, wide range (EPOCH_START..today),
  // and using it here keeps the baseline and the poll on exactly the same query.
  //
  // Reading the filter and the rookie preference from their refs (rather than
  // from the render closure) means the value used is the one in force when the
  // request is fired, which is what the poll compares against.
  const recomputeEntryBaseline = async () => {
    const requestId = ++baselineRequestIdRef.current;
    const { sd, ed } = resolveCountDateRange(filterRef.current);
    const total = await fetchTotalEntryCount(sd, ed, showRookieEntriesRef.current);
    // A newer baseline request was started meanwhile — that one describes the
    // current state, so this (older) total must not overwrite it.
    if (requestId !== baselineRequestIdRef.current) return;
    // Session bug: the auto-logout may have fired while this request was in
    // flight. The logout deliberately resets the baseline to 0; writing a
    // logged-in-era total over it here would leave a bogus baseline behind for
    // whoever signs in next.
    if (!isAuthenticatedRef.current) return;
    if (total >= 0) baselineTotalRef.current = total;
  };

  // Ticket 1d: clears the badge AND moves the baseline in the same synchronous
  // step, so a poll tick firing before the network round trip finishes cannot
  // resurrect the number that was just dismissed.
  //
  // The baseline only ever moves forward: lastPolledTotalRef can be behind the
  // real total (it is at most one tick old) but never ahead of what the reader
  // has already been shown, and letting the baseline drop would turn entries
  // that were already counted into "new" ones again.
  const clearNewEntryBadge = () => {
    setNewEntryCount(0);
    if (lastPolledTotalRef.current > baselineTotalRef.current) {
      baselineTotalRef.current = lastPolledTotalRef.current;
    }
  };

  // Runs a single list pane's refresh behind the same global dim LoadingOverlay
  // the matches pane uses, then resets the badge + baseline, so Yenile behaves
  // identically across all four panes (people/clubs/teams/topics).
  const runPaneRefresh = async (refreshPane: () => Promise<void> | void) => {
    setIsRefreshingPane(true);
    await refreshPane();
    clearNewEntryBadge();
    await recomputeEntryBaseline();
    setIsRefreshingPane(false);
  };

  const handleRefreshPeople = () => runPaneRefresh(loadPeople);
  const handleRefreshClubs  = () => runPaneRefresh(refreshClubs);
  const handleRefreshTeams  = () => runPaneRefresh(refreshTeams);
  // Yenile on the Gündem pane also reloads the entries of the topic currently
  // open on the right, so one click refreshes both panes instead of leaving the
  // reader with a fresh badge next to a stale entry list.
  const handleRefreshTopics = () => {
    setTopicReloadToken((prev) => prev + 1);
    return runPaneRefresh(refreshTopics);
  };

  // Same "also refresh the open topic's entries" behavior as Gündem's Yenile —
  // the topic on the right can belong to any of the three custom-topic tabs.
  const handleRefreshIstikram = () => {
    setTopicReloadToken((prev) => prev + 1);
    return runPaneRefresh(refreshIstikramTopics);
  };

  const handleRefreshBirak = () => {
    setTopicReloadToken((prev) => prev + 1);
    return runPaneRefresh(refreshBirakTopics);
  };

  // /gundem/:slug is shared by Gündem, İstikram, and Bırak topics — after
  // posting a new entry there, the freshly-posted-to topic could be on any
  // of the three lists, so all three refresh silently to keep their
  // recentEntryCount badges current. No global spinner/badge reset here
  // (unlike handleRefreshTopics/Istikram/Birak, which back the explicit
  // Yenile button) — this mirrors what onEntryPosted did before the split.
  const handleCustomTopicEntryPosted = () => {
    refreshTopics();
    refreshIstikramTopics();
    refreshBirakTopics();
  };


  /**
   * Stable key representing the user-visible filter state.
   * Changes ONLY when the user explicitly changes date/sport/gender/sort.
   * Does NOT change when preferences reload (showRookieEntries, matchesPerPage)
   * or when "load more" appends extra matches.  Passed to useMatchSelection so
   * it can redirect to the first match only on genuine filter changes.
   */
  const filterKey = useMemo(() => {
    const sports = [...filter.sportTypes].sort().join(",");
    const genders = [...filter.matchGenders].sort().join(",");
    const start = filter.startDate ? formatDateForAPI(filter.startDate) : "";
    const end = filter.endDate ? formatDateForAPI(filter.endDate) : "";
    const country = filter.country ?? "";
    return `${filter.dateFilterType}:${sports}:${genders}:${start}:${end}:${country}:${sortBy}`;
  }, [filter, sortBy]);

  const {
    selectedMatchId,
    selectedMatch,
    isWaitingForInitialMatch,
    selectAndNavigateToMatch,
  } = useMatchSelection(matches, loadingMatches, filterKey);

  // Match entry page number lives in the URL ("?sayfa=2"), same as the other
  // four domains, so a page can be linked to and the back button returns to it.
  const { page: urlPage, setPage: setUrlPage } = useUrlPage();

  // Sets document.title / meta description / canonical / OG tags for the
  // currently selected match. Unlike the other 4 domains, matches have no
  // dedicated page wrapper component (see useMatchSelection) — the match is
  // just root-level app state — so this lives here instead of in a *Page.tsx.
  // The match slug already IS the full URL path (see Match.slug on the
  // backend), so the canonical URL is the site root + slug, no "/mac/" prefix.
  const matchHomeName = selectedMatch ? resolveClubName({
    shortName: selectedMatch.homeTeam,
    nameTr: selectedMatch.homeTeamNameTr,
    nameEn: selectedMatch.homeTeamNameEn,
  }) : null;
  const matchAwayName = selectedMatch ? resolveClubName({
    shortName: selectedMatch.awayTeam,
    nameTr: selectedMatch.awayTeamNameTr,
    nameEn: selectedMatch.awayTeamNameEn,
  }) : null;
  const matchScoreLabel = selectedMatch && selectedMatch.status !== MatchStatusEnum.SCHEDULED
    ? `${selectedMatch.homeScore}-${selectedMatch.awayScore}`
    : "-";
  useDocumentMeta({
    title: (selectedMatch && matchHomeName && matchAwayName)
      ? `${matchHomeName} ${matchScoreLabel} ${matchAwayName}${pageSuffixes(urlPage).titleSuffix} - Üç Korner Bir Penaltı`
      : undefined,
    description: (selectedMatch && matchHomeName && matchAwayName)
      ? `${matchHomeName} - ${matchAwayName} maçı hakkında yazılan tanımları oku, sen de yorumunu ekle, tartış ve oyla.` +
        (selectedMatch.league ? ` ${selectedMatch.league}` : "") +
        ` (${formatMatchDateLocal(selectedMatch.matchDate, selectedMatch.matchTime)}).`
      : undefined,
    canonicalUrl: (selectedMatch && selectedMatch.slug)
      ? `https://uckornerbirpenalti.com/${selectedMatch.slug}${pageSuffixes(urlPage).urlSuffix}`
      : undefined,
  });

  // ===== Toolbar state (sort, filters, keyword) =====
  // Managed here so sort/filter survive pagination changes.
  // Reset to defaults whenever the selected match changes.
  const [entrySort, setEntrySort] = useState<"oldest" | "newest" | "popular">("oldest");
  const [filterToday, setFilterToday] = useState(false);
  const [rookieOnly, setRookieOnly] = useState(false);
  const [entryKeyword, setEntryKeyword] = useState("");

  // Reset all toolbar params when the user switches to a different match
  useEffect(() => {
    setEntrySort("oldest");
    setFilterToday(false);
    setRookieOnly(false);
    setEntryKeyword("");
  }, [selectedMatchId]);

  // Pass showRookieEntries to useEntries for server-side filtering (proper pagination)
  // Issue B: also destructure updateEntries to support optimistic removal after self-delete
  const {
    entries,
    error: entriesError,
    pagination: entriesPagination,
    loadEntries: paginateEntries,
    loadMoreEntries,
    isLoadingMore: isLoadingMoreEntries,
    updateEntries,
  } = useEntries(selectedMatchId, showRookieEntries, entrySort, filterToday, rookieOnly, entryKeyword, entriesPerPage, urlPage);

 
  // Only active in the default view: oldest sort, no filters, no keyword.
  // An address that names a page ("?sayfa=4") or an entry ("#entry-1499") is a
  // deep link, and the collapsed view cannot serve it: it renders only today's
  // entries and hides the pagination controls, so the requested page was
  // fetched and never shown and the anchored entry was never in the DOM for
  // the scroll to find. An explicit request wins over the default view.
  const isEntryDeepLink = useIsEntryDeepLink();
  const entryCollapseEligible =
    entrySort === "oldest" && !filterToday && !rookieOnly && !entryKeyword.trim() && !isEntryDeepLink;

  // Fetcher used by the collapse hook — always oldest sort, viewer's rookie preference
  const matchCollapseFetcher = useCallback<CollapseEntryFetcher>(async (page, size, fToday) => {
    if (!selectedMatchId) throw new Error("No match selected");
    const res = await fetchEntriesByMatchId(selectedMatchId, page, size, showRookieEntries, "oldest", fToday, false, "");
    return { entries: res.entries, totalItems: res.totalItems, totalPages: res.totalPages };
  }, [selectedMatchId, showRookieEntries]);

  const entryCollapse = useCollapsedOldEntries(selectedMatchId, entryCollapseEligible, entriesPerPage, matchCollapseFetcher);

  // Permalink view for matches: "?tanim=747" shows that one tanım framed by
  // "{n} entry daha" bars. Reuses the same oldest-sort fetcher the collapse
  // hook uses, so no new match-specific request path is introduced.
  const focusEntryId = useFocusEntryId();
  const exitFocus = useExitFocus();
  const entryFocus = useFocusedEntry(selectedMatchId, focusEntryId, entriesPerPage, matchCollapseFetcher);
  const handleExitFocus = useCallback(() => {
    if (entryFocus.entry) exitFocus(entryFocus.page, entryFocus.entry.id);
  }, [exitFocus, entryFocus.entry, entryFocus.page]);

  // ===== New entry count polling =====
  // Polls total entry count across all matches in the current filter.
  // When the count changes, badge appears on Yenile button.
  // Only active while a user is logged in — guests never see the badge.
  const [newEntryCount, setNewEntryCount] = useState(0);
  const baselineTotalRef = useRef<number>(0);

  // Ticket 1d: the total returned by the most recent poll tick.
  //
  // The badge is a diff (currentTotal - baseline), not an event counter, so it
  // is recomputed from scratch on every tick. Clearing it therefore only sticks
  // if the baseline moves at the same moment. Re-fetching the count over the
  // network takes time, and a tick landing in that window compares against the
  // old baseline and puts the very same number back on the button. Snapping the
  // baseline to this already-known total is synchronous, so there is no window.
  const lastPolledTotalRef = useRef<number>(0);

  // Ticket 1d: sequence number of the most recently STARTED baseline fetch.
  // Two baseline requests can be in flight at once (a pane Yenile and the
  // [matches] effect, for instance). Without this the slower one wins by
  // arriving last and can install an older, smaller total as the baseline —
  // which makes the next tick show a positive diff for entries already seen.
  const baselineRequestIdRef = useRef(0);

  // Refs so the interval callback always sees current filter values
  const filterRef = useRef(filter);
  const showRookieEntriesRef = useRef(showRookieEntries);
  filterRef.current = filter;
  showRookieEntriesRef.current = showRookieEntries;

  // Live authentication state for the async count callbacks.
  //
  // Session bug: the inactivity/max-age auto-logout can land while a count
  // request is already in flight. Clearing the interval in the effect cleanup
  // does NOT cancel that pending promise — it still resolves afterwards, and
  // back then it wrote its result into the badge. By that time the logout had
  // already reset the baseline to 0, so the diff became "the whole total"
  // (e.g. 588) and stayed frozen on the button, because polling had stopped and
  // nothing was left to recompute it. Reading the auth state from a ref (the
  // closure captured at interval-creation time is always stale) lets every
  // async continuation below drop its result when the session ended meanwhile.
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  // The count endpoint always requires a concrete window — but the "all" filter
  // (Tümü) legitimately means "no bound", so getDateRangeForFilter returns
  // null/null for it. Without a fallback, both effects below used to silently
  // skip the request every single tick whenever "all" was selected
  // (formatInstantForAPI(null) -> null -> the `if (!sd || !ed) return;` guard),
  // so the "Yenile" badge simply never fired for anyone browsing with no date
  // filter — no error, no request, nothing to see in the network tab either.
  // EPOCH_START stands in for "the beginning of time" so "all" still resolves
  // to a real, wide range instead of being skipped.
  //
  // The bounds are UTC instants, not calendar dates: the left pane asks
  // /api/matches/filter for the viewer's LOCAL day window, and the badge has to
  // count entries on exactly the matches that window returns. Sending dates here
  // while the pane sends instants made the two disagree about matches sitting on
  // the edge of the window.
  const EPOCH_START = "1970-01-01T00:00:00";
  const resolveCountDateRange = (f: MatchFilter): { sd: string; ed: string } => {
    const { startDate, endDate } = getDateRangeForFilter(f.dateFilterType, f.startDate, f.endDate);
    const sd = formatInstantForAPI(startDate);
    const ed = formatInstantForAPI(endDate);
    if (sd && ed) return { sd, ed };
    // Open-ended window: fall back to "since the epoch" / "up to the end of the
    // viewer's today", still expressed as instants.
    const todayEnd = getDateRangeForFilter("today").endDate;
    const today = formatInstantForAPI(todayEnd) as string;
    return { sd: sd ?? EPOCH_START, ed: ed ?? today };
  };

  // Update baseline whenever matches load by fetching from the same count endpoint.
  // This ensures the baseline uses the same exclusion logic (current user excluded)
  // as the polling ticks, so the diff is always apples-to-apples.
  // Only runs when authenticated — guests do not poll at all.
  //
  // Ticket 1d: showRookieEntries is now a dependency. The baseline was fetched
  // with whatever value the flag had when `matches` last changed, while the poll
  // always uses the current one via showRookieEntriesRef. Once the preference
  // loaded late or the reader toggled it, the two sides were counting different
  // sets of entries and the diff carried a permanent offset — a badge showing
  // the same number again after every click, no matter who had written what.
  useEffect(() => {
    // The badge is cleared together with a forward move of the baseline, so a
    // tick firing before the fetch below returns cannot re-show the old number.
    clearNewEntryBadge();
    if (!isAuthenticated) return;

    const requestId = ++baselineRequestIdRef.current;
    const { sd, ed } = resolveCountDateRange(filter);

    fetchTotalEntryCount(sd, ed, showRookieEntries).then((total) => {
      if (requestId !== baselineRequestIdRef.current) return;
      // Session bug: same reason as in recomputeEntryBaseline — a baseline that
      // arrives after the session ended must not survive the logout reset.
      if (!isAuthenticatedRef.current) return;
      if (total >= 0) baselineTotalRef.current = total;
    }).catch(() => {
      // Fallback to local sum if the request fails
      if (requestId !== baselineRequestIdRef.current) return;
      if (!isAuthenticatedRef.current) return;
      baselineTotalRef.current = matches.reduce((sum, m) => sum + (m.entryCount ?? 0), 0);
    });
  }, [matches, isAuthenticated, showRookieEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for new entries every N seconds — single lightweight COUNT query.
  // Skipped entirely when no user is logged in so guests never see a stale badge.
  useEffect(() => {
    if (!isAuthenticated) {
      // Reset badge when the user logs out
      setNewEntryCount(0);
      // Ticket 1d: both totals were counted with the previous viewer's own
      // entries excluded, so they mean nothing for whoever logs in next.
      // Leaving them behind would seed the new session with a bogus diff.
      baselineTotalRef.current = 0;
      lastPolledTotalRef.current = 0;
      return;
    }

    // Session bug: clearInterval stops FUTURE ticks but cannot cancel the fetch
    // of a tick that is already awaiting a response. That pending tick used to
    // resolve after the logout had reset the baseline to 0 and compute
    // diff = currentTotal - 0, i.e. the entire total, then paint it on a button
    // that no longer polls — the huge frozen number on the Yenile badge of a
    // logged-out session. This flag marks every tick started by this effect run
    // as void once the effect is torn down (logout, or any auth change).
    let cancelled = false;

    const interval = setInterval(async () => {
      try {
        // A tick can also fire in the short window between the logout clearing
        // the stored token and React re-running this effect. Without the token
        // the backend counts every entry (nothing to exclude), which is exactly
        // the inflated total that used to reach the badge — so no request is
        // sent at all once the session is gone.
        if (cancelled || !isAuthenticatedRef.current) return;
        const { sd, ed } = resolveCountDateRange(filterRef.current);
        const currentTotal = await fetchTotalEntryCount(sd, ed, showRookieEntriesRef.current);
        // Re-check AFTER the await: the session may have ended while the request
        // was on the wire. Both the badge and the "last polled" total are then
        // meaningless — they were counted for a viewer who is no longer signed
        // in — so this tick is dropped without touching any state.
        if (cancelled || !isAuthenticatedRef.current) return;
        if (currentTotal < 0) return;
        // Ticket 1d: remember the total this tick was based on, so dismissing the
        // badge can move the baseline forward without waiting for a new request.
        lastPolledTotalRef.current = currentTotal;
        const diff = currentTotal - baselineTotalRef.current;
        setNewEntryCount(diff > 0 ? diff : 0);
      } catch {
        // Silently ignore polling errors
      }
    }, NEW_ENTRY_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  // Combined refresh: refreshes matches (which resets baseline) and entries.
  // The dim overlay stays visible for the whole operation via isManualRefreshing.
  const handleRefresh = useCallback(async () => {
    setIsManualRefreshing(true);
    // Ticket 1d: baseline moves with the badge, and is then confirmed against the
    // server. Relying on the [matches] effect alone was racy: its fetch is fired
    // asynchronously, and a poll tick landing before it returned compared against
    // the old baseline and put the dismissed number straight back.
    clearNewEntryBadge();
    try {
      await refreshMatches();
      if (selectedMatchId) {
        await paginateEntries(1);
      }
      await recomputeEntryBaseline();
    } finally {
      setIsManualRefreshing(false);
    }
  }, [refreshMatches, selectedMatchId, paginateEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Route transition overlay: shows a brief dim when navigating to another
  // page so the destination content settles behind a fade instead of visibly
  // popping/jumping into place. The ref skips the very first render.
  const [isRouteTransitioning, setIsRouteTransitioning] = useState(false);
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathnameRef.current === location.pathname) return;
    prevPathnameRef.current = location.pathname;
    setIsRouteTransitioning(true);
    const timer = setTimeout(() => setIsRouteTransitioning(false), 450);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Mobile panel state: derived from URL so it's always in sync with navigation.
  // Any person/club/team/match content page shows the entries pane; the root path
  // (match list) shows the list pane. A separate override state handles the case
  // where the user explicitly taps the back button from a content page back to root.
  const [mobileBackToList, setMobileBackToList] = useState(false);

  // Reset the back-override whenever the URL moves to a content page, so navigating
  // away from root and then pressing back works correctly on subsequent visits.
  const isContentPage = isPersonPage || isClubPage || isTeamPage || isTopicPage || isProfilePage ||
    isSettingsPage || isTrashPage || isDraftsPage || isModlogPage || isFeedPage ||
    isStatisticsPage || isContactPage || isLegalPage || isSearchPage || isEntryPermalinkPage;

  // Home navigation flag: "/" is immediately redirected to the default topic
  // (a content page) by useMatchSelection, which used to make every return to
  // the home screen open that topic's ENTRIES on mobile — the user had to
  // press back each time. When home navigation is the cause of landing on a
  // content page, this ref tells the effect below to leave the list visible.
  const homeNavigationRef = useRef(false);

  // True for every URL that has its own content on the right: the content pages
  // above plus match pages, whose address is a bare "/{slug}" at the root level.
  const showsContentPane = isContentPage || location.pathname !== "/";

  // The effect below used to depend on isContentPage alone, so it only ran when
  // that flag FLIPPED. Navigating from one content page straight to another —
  // exactly what picking a result from the header search dropdown does — left
  // mobileBackToList at whatever the previous screen set it to. After a mobile
  // back tap that value is true, so the search result opened behind the list
  // pane and the reader saw nothing happen until a manual page refresh.
  // Depending on the pathname (and the query string, which is what distinguishes
  // the search results page) makes every real navigation reveal the content pane.
  useEffect(() => {
    if (!showsContentPane) return;
    if (homeNavigationRef.current) {
      // Landed on a content page only because "/" redirected there after a
      // home click — keep showing the LIST pane on mobile.
      homeNavigationRef.current = false;
      return;
    }
    // User navigated to a content page — clear the back override so pane shows correctly
    setMobileBackToList(false);
  }, [showsContentPane, location.pathname, location.search]);

  // Called by the Header on brand/logo (and header background) clicks.
  // Flips the mobile view to the list and arms the ref above so the "/"
  // redirect's content page does not flip it back.
  const handleHomeNavigate = useCallback(() => {
    homeNavigationRef.current = true;
    setMobileBackToList(true);
  }, []);

  // Determine which pane to show on mobile:
  // - Content pages always show the entries/content pane
  // - Root path shows list unless a match was selected
  const mobilePaneClass = mobileBackToList
    ? "mobile-show-list"
    : isContentPage
    ? "mobile-show-entries"
    : selectedMatch
    ? "mobile-show-entries"
    : "mobile-show-list";

  // Keep a ref to the current matches array so handleSelectMatch can read it
  // without listing `matches` as a useCallback dependency.
  // If `matches` were in the dep array, handleSelectMatch would get a new
  // reference on every poll tick — breaking memo on all 300 MatchRow components.
  const matchesRef = useRef(matches);
  matchesRef.current = matches;

  // Keep a ref to selectAndNavigateToMatch for the same reason — it is not
  // wrapped in useCallback inside useMatchSelection, so its reference changes
  // on every AppContent render.
  const selectAndNavigateRef = useRef(selectAndNavigateToMatch);
  selectAndNavigateRef.current = selectAndNavigateToMatch;

  const handleSelectMatch = useCallback((matchId: number) => {
    const match = matchesRef.current.find((m) => m.id === matchId);
    if (match) {
      selectAndNavigateRef.current(match);
      // On mobile: clear back override so entries pane becomes visible
      homeNavigationRef.current = false;
      setMobileBackToList(false);
    }
  }, []); // empty deps — reads latest values via refs, never recreated

  // Called by PersonRow / ClubRow / TeamRow — URL change already triggers content pane via isContentPage
  const handleMobileNavigate = useCallback(() => {
    // Explicit navigation to content — discard any pending home-click flag
    homeNavigationRef.current = false;
    setMobileBackToList(false);
  }, []);

  // Called by the full-width DomainTabBar. Switches the left pane's domain and
  // — on mobile — flips the visible pane back to the list, so tapping a domain
  // while reading a content page always lands on that domain's list. On desktop
  // the back-override class has no visual effect (its CSS lives inside the
  // mobile media query only). Deliberately does NOT navigate, same as
  // handleMobileBack: useMatchSelection would bounce a bare "/" straight back.
  const handleDomainChange = useCallback((mode: "matches" | "people" | "clubs" | "teams" | "topics" | "istikram" | "birak") => {
    setPaneMode(mode);
    setMobileBackToList(true);
  }, []);

  // Filters live in the header, right next to the main search bar — always
  // visible without hunting a corner for them. Mounted only on the main
  // layout: admin/messages pages have their own toolbars and no pane filters.
  // Memoized because Header is wrapped in React.memo — recreating this element
  // on every AppContent render (poll ticks etc.) would defeat that memo.
  const headerFilterSlot = useMemo(() => {
    if (isAdminPage || isMessagesPage) return undefined;
    return (
      <PaneFilterBar
        paneMode={paneMode}
        matchSportTypes={filter.sportTypes}
        matchDateFilterType={filter.dateFilterType}
        matchStartDate={filter.startDate}
        matchEndDate={filter.endDate}
        matchCountry={filter.country}
        matchLiveOnly={filter.liveOnly}
        onMatchFilterChange={applyFilter}
        clubsCountry={clubsCountryFilter}
        onClubsCountryChange={handleClubsCountryFilterChange}
        peopleCountry={peopleCountryFilter}
        onPeopleCountryChange={handlePeopleCountryFilterChange}
        teamsCountry={teamsCountryFilter}
        onTeamsCountryChange={handleTeamsCountryFilterChange}
        topicsChannelId={topicsChannelFilter}
        onTopicsChannelChange={handleTopicsChannelFilterChange}
      />
    );
  }, [
    isAdminPage, isMessagesPage, paneMode,
    filter, applyFilter,
    clubsCountryFilter, handleClubsCountryFilterChange,
    peopleCountryFilter, handlePeopleCountryFilterChange,
    teamsCountryFilter, handleTeamsCountryFilterChange,
    topicsChannelFilter, handleTopicsChannelFilterChange,
  ]);

  // Called by PersonPage / ClubPage / TeamPage back button on mobile.
  // Only flips the mobile pane back to the list and restores the matching left
  // pane mode — deliberately does NOT navigate.
  //
  // It used to call navigate("/"), which looked like it worked for a moment and
  // then bounced straight back: useMatchSelection treats a bare "/" as the
  // landing route and redirects it to the most recently active Gündem topic
  // with replace: true, so the user ended up on the page they just left with no
  // history entry to go back to. Keeping the current URL avoids that entirely;
  // the pane override is cleared again as soon as the user opens something from
  // the list (handleSelectMatch / handleMobileNavigate).
  const handleMobileBack = useCallback(() => {
    setMobileBackToList(true);
    setPaneMode(
      isPersonPage ? "people" :
      isClubPage   ? "clubs"  :
      isTeamPage   ? "teams"  :
      // A custom-topic detail page can have been opened from Gündem, İstikram
      // or Bırak — all three share the /gundem/:slug route. If the pane was
      // already sitting on one of the three when the page opened, keep it
      // there instead of collapsing back to Gündem specifically.
      isTopicPage  ? (paneMode === "istikram" || paneMode === "birak" ? paneMode : "topics") :
      paneMode
    );
  }, [isPersonPage, isClubPage, isTeamPage, isTopicPage, paneMode]);

  const handlePostEntry = async (content: string) => {
    if (!selectedMatchId) {
      console.error("No match selected");
      return;
    }

    if (!user) {
      console.error("User not authenticated");
      return;
    }

    try {
      const createdEntry = await postNewEntry(content, selectedMatchId);

      // Collapsed view is chronological (oldest first) — append the new entry to its end
      // so it appears just above the input form without leaving collapsed mode
      if (entryCollapse.active) {
        entryCollapse.updateEntries(prev => [...prev, createdEntry]);
      }

      // Pass showRookieEntries and toolbar params to fetchEntriesByMatchId for consistent filtering
      const response = await fetchEntriesByMatchId(
        selectedMatchId,
        1,
        entriesPerPage,
        showRookieEntries,
        entrySort,
        filterToday,
        rookieOnly,
        entryKeyword
      );

      paginateEntries(response.totalPages);
    } catch (error) {
      // Re-throw the error as-is to preserve RateLimitError type for toast handling
      throw error;
    }
  };

  // Issue B: optimistically remove the soft-deleted entry from local state immediately.
  // The backend has already marked it as deleted; we just filter it out of the UI
  // so the user sees it disappear instantly without a full re-fetch.
  const handleDeleteEntry = (entryId: number) => {
    updateEntries((prev) => prev.filter((e) => e.id !== entryId));
    // Keep the collapsed list in sync as well
    entryCollapse.updateEntries((prev) => prev.filter((e) => e.id !== entryId));
  };

  // Email verification page — render standalone before any other checks
  if (isVerifyEmailPage) {
    return <VerifyEmailPage />;
  }

  // Password reset page — render standalone before any other checks
  if (isResetPasswordPage) {
    return <ResetPasswordPage />;
  }

  // Block non-admin users when maintenance mode is active.
  // Placed BEFORE the loading / error early-returns on purpose: those could
  // swallow the maintenance page (an API error rendered ErrorDisplay instead,
  // leaving no way to reach the login form and therefore no way back in), and
  // there is no point loading pane data for a visitor who will never see it.
  const isUserAdmin = user?.role?.toUpperCase() === "ADMIN";
  if (maintenanceEnabled && !isUserAdmin) {
    return <MaintenancePage />;
  }

  // Initial hard load (browser refresh / first visit): there is no content on
  // screen yet, so a transparent dim would just look like a solid gray page.
  // Show the plain SpinnerLoading exactly as before. The dim overlay below is
  // used only once real content is rendered underneath.
  if (!hasLoadedOnce && loadingMatches && isWaitingForInitialMatch) {
    return <SpinnerLoading />;
  }

  // Global dim overlay flag: covers manual Yenile refreshes (matches, people,
  // clubs, teams panes) and route transitions. The layout stays mounted
  // underneath (no full-page SpinnerLoading swap), so the existing content is
  // visible through the transparent dim and nothing jumps into place.
  const isGlobalLoading =
    (loadingMatches && isWaitingForInitialMatch) ||
    isRefreshingPane ||
    isManualRefreshing ||
    isRouteTransitioning;

  const error = matchesError || entriesError;
  if (error) return <ErrorDisplay error={error} />;

  // LeftPane's topic-list props (topics, pagination, sort, refresh) are shared
  // by all three custom-topic tabs — only one is ever visible at a time, so
  // rather than threading three parallel sets of props through LeftPane, pick
  // the one matching the current paneMode here and pass it through the same
  // prop names LeftPane already had for "topics" (Gündem). selectedTopicId /
  // onSelectTopic stay a single shared concept regardless of which tab the
  // open topic came from.
  const activeTopicsDomain =
    paneMode === "istikram"
      ? {
          topics: istikramTopics,
          loadMoreTopics: loadMoreIstikramTopics,
          isLoadingMoreTopics: isLoadingMoreIstikramTopics,
          topicsTotalPages: istikramTopicsTotalPages,
          topicsCurrentPage: istikramTopicsCurrentPage,
          topicsSortBy: istikramTopicsSortBy,
          onTopicsSortChange: handleIstikramTopicsSortChange,
          onRefreshTopics: handleRefreshIstikram,
          onShuffleTopics: shuffleIstikramTopics,
        }
      : paneMode === "birak"
      ? {
          topics: birakTopics,
          loadMoreTopics: loadMoreBirakTopics,
          isLoadingMoreTopics: isLoadingMoreBirakTopics,
          topicsTotalPages: birakTopicsTotalPages,
          topicsCurrentPage: birakTopicsCurrentPage,
          topicsSortBy: birakTopicsSortBy,
          onTopicsSortChange: handleBirakTopicsSortChange,
          onRefreshTopics: handleRefreshBirak,
          onShuffleTopics: shuffleBirakTopics,
        }
      : {
          topics,
          loadMoreTopics,
          isLoadingMoreTopics,
          topicsTotalPages,
          topicsCurrentPage,
          topicsSortBy,
          onTopicsSortChange: handleTopicsSortChange,
          onRefreshTopics: handleRefreshTopics,
          onShuffleTopics: shuffleTopics,
        };

  // Pull-to-refresh dispatch: the gesture has to do whatever the pane's own
  // Yenile button does, which differs per domain.
  // Not wrapped in useCallback: this sits after the early returns above, where
  // a hook would break the rules-of-hooks ordering. PullToRefresh keeps the
  // callback in a ref, so a new function identity per render costs nothing.
  const handlePullToRefresh = () => {
    switch (paneMode) {
      case "people":
        return handleRefreshPeople();
      case "clubs":
        return handleRefreshClubs();
      case "teams":
        return handleRefreshTeams();
      case "topics":
      case "istikram":
      case "birak":
        return activeTopicsDomain.onRefreshTopics();
      default:
        return handleRefresh();
    }
  };

  return (
    <div className="app-container">
      {/* Global dim overlay — fades in/out over the mounted layout during
          loads, refreshes and route transitions (no logo, just a slight dim) */}
      <LoadingOverlay visible={isGlobalLoading} />
      {/* External link warning page — full-screen, no header/menu needed.
          Header/banner were previously always rendered here regardless of
          isExternalLinkPage, which is why the warning box could sit far
          below the fold: they still took up their normal document-flow
          space above the (position: fixed) overlay, on top of everything. */}
      {!isExternalLinkPage && <Header filterSlot={headerFilterSlot} onHomeNavigate={handleHomeNavigate} />}
      {/* Connection strip — renders nothing while the backend is reachable.
          Placed above the announcement banner so a connection problem is the
          first thing the user sees. */}
      <ConnectivityBanner />
      {/* New version available strip (PWA Ticket 4) — renders nothing unless a
          newer service worker is waiting after a deploy. Kept outside the
          isExternalLinkPage guard so an outdated build is always reported. */}
      <UpdateAvailableBanner />
      {/* Pull-to-refresh (installed PWA only). Disabled on the admin, messages
          and external-link screens: those manage their own scrolling and the
          gesture would fight with it. */}
      <PullToRefresh
        onRefresh={handlePullToRefresh}
        enabled={!isAdminPage && !isMessagesPage && !isExternalLinkPage}
      />
      {/* Announcement banner — shown to all users when enabled by admin */}
      {!isExternalLinkPage && <AnnouncementBanner />}
      {/* Admin page has its own full-width layout */}
      {isExternalLinkPage ? (
        <ExternalLinkWarning />
      ) : isAdminPage ? (
        <div className="admin-page-wrapper">
          <Routes>
            <Route path="/admin/*" element={<AdminPage />} />
          </Routes>
        </div>
      ) : isMessagesPage ? (
        <div className="messages-page-wrapper">
          <Routes>
            <Route path="/mesajlar" element={<MessagesPage />} />
          </Routes>
        </div>
      ) : (
        <>
          {/* Full-width domain bar: Gündem / Maç / Kulüp / Kişi / Takım.
              Replaces the mode toggle that used to live inside the left pane —
              the five domains now read as top-level sections and the bar can
              grow horizontally as new domains/buttons are added. The filters
              (PaneFilterBar) render in the header next to the search bar, so
              the left pane stays a bare list toolbar: Son | Bugün | + | Yenile. */}
          <DomainTabBar paneMode={paneMode} onDomainChange={handleDomainChange} />
          <div className={`content-wrapper ${mobilePaneClass}`}>
          <LeftPane
            paneMode={paneMode}
            matches={matches}
            currentPage={matchesPagination.currentPage}
            totalPages={matchesPagination.totalPages}
            totalItems={matchesPagination.totalItems}
            paginate={(page) => paginateMatches(page)}
            onSelectMatch={handleSelectMatch}
            selectedMatchId={selectedMatchId}
            onRefresh={handleRefresh}
            sortBy={sortBy}
            setSortBy={setSortBy}
            newEntryCount={newEntryCount}
            loadMoreMatches={loadMoreMatches}
            isLoadingMore={isLoadingMoreMatches}
            people={people}
            selectedPersonId={selectedPersonId}
            onSelectPerson={handleSelectPerson}
            onRefreshPeople={handleRefreshPeople}
            loadMorePeople={loadMorePeople}
            isLoadingMorePeople={isLoadingMorePeople}
            peopleTotalPages={peopleTotalPages}
            peopleCurrentPage={peopleCurrentPage}
            peopleSortBy={peopleSortBy}
            onPeopleSortChange={handlePeopleSortChange}
            clubs={clubs}
            selectedClubId={selectedClubId}
            onSelectClub={handleSelectClub}
            onRefreshClubs={handleRefreshClubs}
            loadMoreClubs={loadMoreClubs}
            isLoadingMoreClubs={isLoadingMoreClubs}
            clubsTotalPages={clubsTotalPages}
            clubsCurrentPage={clubsCurrentPage}
            clubsSortBy={clubsSortBy}
            onClubsSortChange={handleClubsSortChange}
            teams={teams}
            selectedTeamId={selectedTeamId}
            onSelectTeam={handleSelectTeam}
            onRefreshTeams={handleRefreshTeams}
            loadMoreTeams={loadMoreTeams}
            isLoadingMoreTeams={isLoadingMoreTeams}
            teamsTotalPages={teamsTotalPages}
            teamsCurrentPage={teamsCurrentPage}
            teamsSortBy={teamsSortBy}
            onTeamsSortChange={handleTeamsSortChange}
            topics={activeTopicsDomain.topics}
            selectedTopicId={selectedTopicId}
            onSelectTopic={handleSelectTopic}
            onRefreshTopics={activeTopicsDomain.onRefreshTopics}
            onShuffleTopics={activeTopicsDomain.onShuffleTopics}
            loadMoreTopics={activeTopicsDomain.loadMoreTopics}
            isLoadingMoreTopics={activeTopicsDomain.isLoadingMoreTopics}
            topicsTotalPages={activeTopicsDomain.topicsTotalPages}
            topicsCurrentPage={activeTopicsDomain.topicsCurrentPage}
            topicsSortBy={activeTopicsDomain.topicsSortBy}
            onTopicsSortChange={activeTopicsDomain.onTopicsSortChange}
            onMobileNavigate={handleMobileNavigate}
          />
          
          {isPersonPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/kisi/:slug" element={<PersonPageWrapper onMobileBack={handleMobileBack} />} />
              </Routes>
            </div>
          ) : isClubPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/kulup/:slug" element={<ClubPageWrapper onEntryPosted={refreshClubs} onMobileBack={handleMobileBack} />} />
              </Routes>
            </div>
          ) : isTeamPage ? (
            <div className="main-content">
              <Routes>
                {/* Canonical single-segment address, e.g.
                    /takim/galatasaray-futbol--372. Same shape as the other four
                    domains. A bare id also lands here, which is what entry
                    references store. */}
                <Route path="/takim/:teamSlug" element={<TeamPageWrapper onEntryPosted={refreshTeams} onMobileBack={handleMobileBack} />} />
                {/* Legacy two-segment address kept so links shared or indexed
                    before the change still resolve; the backend answers 301 and
                    the page navigates to the canonical address. */}
                <Route path="/takim/:clubSlug/:teamSlug" element={<TeamPageWrapper onEntryPosted={refreshTeams} onMobileBack={handleMobileBack} />} />
              </Routes>
            </div>
          ) : isTopicPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/gundem/:slug" element={<CustomTopicPageWrapper reloadToken={topicReloadToken} onEntryPosted={handleCustomTopicEntryPosted} onMobileBack={handleMobileBack} />} />
              </Routes>
            </div>
          ) : isProfilePage ? (
            <div className="main-content">
              <Routes>
                <Route path="/yazar/:slug" element={<ProfilePageWrapper />} />
              </Routes>
            </div>
          ) : isSettingsPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/ayarlar" element={<SettingsPage />} />
              </Routes>
            </div>
          ) : isTrashPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/cop-kutusu" element={<TrashPage />} />
              </Routes>
            </div>
          ) : isDraftsPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/taslaklar/*" element={<DraftsPage />} />
              </Routes>
            </div>
          ) : isModlogPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/moderasyon-gunlugu" element={<ModlogPage />} />
              </Routes>
            </div>
          ) : isFeedPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/akis" element={<FeedPage />} />
              </Routes>
            </div>
          ) : isStatisticsPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/istatistikler" element={<StatisticsPage />} />
              </Routes>
            </div>
          ) : isContactPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/iletisim" element={<ContactPage />} />
              </Routes>
            </div>
          ) : isLegalPage ? (
            <div className="main-content">
              <Routes>
                <Route path="/kullanim-kosullari" element={<LegalPage docType="TERMS" />} />
                <Route path="/gizlilik-politikasi" element={<LegalPage docType="PRIVACY" />} />
                <Route path="/cerez-politikasi" element={<LegalPage docType="COOKIE" />} />
                <Route path="/cerez-tercihleri" element={<CookiePreferencesPage />} />
                <Route path="/hamur-abi-kurallari" element={<LegalPage docType="RULES" />} />
              </Routes>
            </div>
          ) : isEntryPermalinkPage ? (
            <div className="main-content">
              <Routes>
                {/* Stable per-entry permalink. Resolves the topic, the page
                    number (for THIS reader's page size) and the anchor, then
                    replaces itself with that address. */}
                <Route path="/entry/:id" element={<EntryPermalinkPage />} />
              </Routes>
            </div>
          ) : isSearchPage ? (
            <div className="main-content">
              <SearchResultsPage />
            </div>
          ) : (
            // Entries are now filtered server-side based on showRookieEntries preference
            // No client-side filtering needed - pagination is correct from backend
            <MainContent
              // Focused permalink view wins over both the collapsed view and
              // the normal list: the reader asked for one specific tanım.
              entries={selectedMatch
                ? (entryFocus.active
                    ? (entryFocus.entry ? [entryFocus.entry] : [])
                    : (entryCollapse.active ? entryCollapse.entries : entries))
                : []}
              focusActive={entryFocus.active}
              focusLoading={entryFocus.loading}
              focusHiddenAbove={entryFocus.hiddenAbove}
              focusHiddenBelow={entryFocus.hiddenBelow}
              onExitFocus={handleExitFocus}
              currentPage={entriesPagination.currentPage}
              totalPages={entriesPagination.totalPages}
              totalItems={entriesPagination.totalItems}
              pageSize={entriesPagination.pageSize}
              paginate={setUrlPage}
              selectedMatch={selectedMatch}
              onPostEntry={handlePostEntry}
              onMobileBack={() => setMobileBackToList(true)}
              onDeleteEntry={handleDeleteEntry}
              sort={entrySort}
              onSortChange={setEntrySort}
              filterToday={filterToday}
              onFilterTodayChange={setFilterToday}
              rookieOnly={rookieOnly}
              onRookieOnlyChange={setRookieOnly}
              keyword={entryKeyword}
              onKeywordChange={setEntryKeyword}
              loadMoreEntries={loadMoreEntries}
              isLoadingMoreEntries={isLoadingMoreEntries}
              collapseActive={entryCollapse.active}
              collapseChecking={entryCollapse.checking}
              collapseHiddenCount={entryCollapse.hiddenCount}
              onLoadOlderEntries={entryCollapse.loadOlderEntries}
              isLoadingOlderEntries={entryCollapse.isLoadingOlder}
            />
          )}
          
          <RightPane />
          </div>
        </>
      )}
      {!isMessagesPage && <Footer />}
      {/* Cookie consent bar — fixed to the viewport bottom, shown until the
          visitor decides. Rendered last so it sits above page content. */}
      {!isExternalLinkPage && <CookieConsentBanner />}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      {/* ConnectivityProvider wraps everything so any provider's own startup
          requests already feed the online/offline state */}
      <ConnectivityProvider>
      <AuthProvider>
        <PreferencesProvider>
          {/* SiteSettingsProvider loads site name/slogan/announcement from backend on startup */}
          <SiteSettingsProvider>
          {/* CookieConsentProvider owns the analytics/ads gate. It sits inside
              SiteSettingsProvider because it needs the cookie-bar enabled flag,
              and outside Router because the footer consumes it on every page. */}
          <CookieConsentProvider>
          <Router>
            <Routes>
              <Route path="/*" element={<AppContent />} />
            </Routes>
          </Router>
          </CookieConsentProvider>
          </SiteSettingsProvider>
        </PreferencesProvider>
      </AuthProvider>
      </ConnectivityProvider>
    </ThemeProvider>
  );
};

export default App;